import axios from 'axios';
import { Dispatch } from 'redux';
import { errorHandler } from 'services/axiosService';

import { AppThunkDispatch, QContext, REACT_APP_QWEALTH_API_V3 } from '@qwealth/qdata';
import { deleteFiles, S3Item } from '@qwealth/qvault';

import { setKypHasError } from 'data/actions/KYP';
import { Kyp, KypFilters } from 'data/models/Kyp';
import { kypFilterDateOptionToDateKeyMap } from 'data/refData/kyp';

export const searchKyps = async (searchQuery: string, dispatch?: Dispatch): Promise<Kyp[]> => {
  try {
    const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/kyp?name,ticker,assessment[like]=${searchQuery}`);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed searching KYPs')(e);
    }
    return [];
  }
};

const buildKypParams = (filters: KypFilters | undefined) => {
  const params = filters
    ? Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          return (
            key != 'filteringDate' && // date fields are handled below
            key != 'startDate' &&
            key != 'endDate' &&
            value != null //Filtering dates are default null/undefined
          );
        }),
      )
    : {};

  if (filters?.reviewStatus === 'expired') {
    params['reviewStatus'] = null;
    params['isExpired'] = true;
  }

  const dateFieldToParamName = filters?.filteringDate ? kypFilterDateOptionToDateKeyMap[filters?.filteringDate] : '';

  if (filters?.startDate) {
    params[`${dateFieldToParamName}[startDate]`] = filters.startDate;
  }

  if (filters?.endDate) {
    params[`${dateFieldToParamName}[endDate]`] = filters.endDate;
  }

  return params;
};

export const fetchKyps = async (filters?: KypFilters, dispatch?: AppThunkDispatch): Promise<Kyp[]> => {
  try {
    const params = buildKypParams(filters);
    const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/kyp`, { params });
    return response.data;
  } catch (e) {
    if (dispatch) {
      dispatch(setKypHasError(true));
      errorHandler(dispatch, 'API Error: Failed Fetching KYPs')(e);
    }
    return [];
  }
};

export const patchKyp = async (kyp: Kyp, dispatch?: Dispatch): Promise<Kyp | undefined> => {
  try {
    const response = await axios.patch(`${REACT_APP_QWEALTH_API_V3}/kyp/${kyp.uuid}`, kyp);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed updating KYP: ${kyp.name}`)(e);
    }
    return undefined;
  }
};

export const postKyp = async (kyp: Kyp, dispatch?: Dispatch): Promise<Kyp | undefined> => {
  try {
    const payload = {
      name: kyp.name,
      type: kyp.type,
      ticker: kyp.ticker,
      partner: kyp.partner,
      isinCode: kyp.isinCode,
      assessment: kyp.assessment,
      assetClass: kyp.assetClass,
      reviewStatus: 'pending',
      isApproved: 0,
    };

    const response = await axios.post(`${REACT_APP_QWEALTH_API_V3}/kyp`, payload);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed posting KYP: ${kyp.name}`)(e);
    }
    return undefined;
  }
};

export const deleteKyp = async (kyp: Kyp, dispatch?: Dispatch): Promise<Kyp | undefined> => {
  try {
    const response = await axios.delete(`${REACT_APP_QWEALTH_API_V3}/kyp/${kyp.uuid}`);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed Deleting KYP: ${kyp.name}`)(e);
    }
    return undefined;
  }
};

export const deleteAllKypFileAttachments = async (attachments: S3Item[], dispatch?: Dispatch): Promise<boolean> => {
  try {
    const keys = attachments.map(a => a.key).filter((key): key is string => Boolean(key));
    await deleteFiles(keys);
    return true;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed to delete KYP File Attachments')(e);
    }
    return false;
  }
};
